/**
 * The Turnstile widget, for the two forms at the donor door.
 *
 * Explicit rendering rather than the `class="cf-turnstile"` kind, because this is a
 * single-page application: the implicit sort scans the document once when the script
 * loads, which is before React has drawn either form and never again afterwards. Here
 * the widget is created when the form mounts and destroyed when it unmounts, and the
 * component keeps the id `turnstile.render()` hands back so it can reset it.
 *
 * Resetting is the part that is easy to leave out and then puzzle over. A token is
 * good for one submission. On a form that navigates away — an ordinary HTML POST — that
 * never matters. Here the page stays exactly where it was after a refused sign-in, so
 * without a reset the second attempt sends a token Cloudflare has already retired and
 * gets a refusal that has nothing to do with the password. That is why `resetKey` is a
 * prop rather than something handled internally: only the form knows that an attempt
 * finished.
 *
 * The script is fetched from challenges.cloudflare.com, and the widget is a frame from
 * there too. The donor door's Content-Security-Policy names that origin in both
 * script-src and frame-src, and nothing else outside our own; moving the widget to
 * another host means changing public/_headers with it, or the policy blocks it.
 */
import { useEffect, useRef, useState } from 'react'

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * Load the script once per document and hand every caller the same promise.
 *
 * Both forms can be mounted in one session — signing in, failing, going to the
 * forgotten-password screen — and the second must not add a second copy of the script.
 * Cloudflare is explicit that the URL has to be fetched as-is rather than proxied or
 * bundled, so this is a `<script>` tag and not an import.
 */
let scriptPromise = null
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = SCRIPT_SRC
    el.async = true
    el.defer = true
    el.onload = () => resolve(window.turnstile)
    el.onerror = () => {
      // Cleared so a later mount can try again — a failed load is usually a network
      // that came back, not a permanent state of the page.
      scriptPromise = null
      reject(new Error('Could not load the “are you human” check.'))
    }
    document.head.appendChild(el)
  })
  return scriptPromise
}

/**
 * @param sitekey  from /api/donor/context; the component renders nothing without one,
 *                 which is how a deployment with the challenge switched off shows no gap
 *                 where a widget would be.
 * @param action   names the form, so a token solved on one cannot be spent on the other.
 * @param onToken  called with the token, and with null when it expires or errors.
 * @param resetKey change it to throw the solved token away and challenge again.
 */
export default function Turnstile({ sitekey, action, onToken, resetKey = 0 }) {
  const box = useRef(null)
  const widget = useRef(null)
  const [failed, setFailed] = useState(null)

  // `onToken` is held in a ref so that a parent re-rendering with a new closure does not
  // tear down and rebuild the widget — which would make the visitor solve it again, and
  // on every keystroke in the email field at that.
  //
  // Written in an effect rather than during the render, which is both the rule and, here,
  // the correct order anyway: effects run in the order they are declared, so this one has
  // already put the current callback in place by the time the one below renders a widget
  // that will call it.
  const emit = useRef(onToken)
  useEffect(() => {
    emit.current = onToken
  })

  useEffect(() => {
    if (!sitekey) return
    let live = true

    loadTurnstile()
      .then((turnstile) => {
        if (!live || !box.current) return
        widget.current = turnstile.render(box.current, {
          sitekey,
          action,
          // The widget reads the page's own light/dark choice rather than the system's,
          // so it does not sit on a dark card wearing a white box.
          theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'auto',
          callback: (token) => emit.current?.(token),
          'expired-callback': () => emit.current?.(null),
          'error-callback': () => emit.current?.(null),
        })
      })
      .catch((err) => live && setFailed(err.message))

    return () => {
      live = false
      if (widget.current && window.turnstile) {
        window.turnstile.remove(widget.current)
        widget.current = null
      }
    }
    // Deliberately not `onToken`: see the ref above.
  }, [sitekey, action])

  useEffect(() => {
    // Nothing to reset on the first render, and resetting a widget that has not been
    // built yet throws.
    if (!resetKey || !widget.current || !window.turnstile) return
    window.turnstile.reset(widget.current)
    emit.current?.(null)
  }, [resetKey])

  if (!sitekey) return null

  if (failed) {
    return (
      <div className="small" role="alert">
        {failed} Check your connection and reload the page.
      </div>
    )
  }

  return <div ref={box} />
}
